import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/components/Analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function InlineLeadCapture() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { pathname } = useLocation();

  async function onSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.includes("@")) {
      toast.error("Please enter your name and a valid email.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("email", email);
      fd.append("page_url", pathname);
      if (file) fd.append("quote", file);
      await axios.post(`${BACKEND}/api/lead-capture`, fd);
      setDone(true);
      trackEvent("generate_lead", {
        source: "seo_landing",
        page: pathname,
        has_file: !!file,
      });
      toast.success("Got it — we'll review and come back to you.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="my-12 rounded-3xl bg-[#E6F4F3] border border-[#BFE3E1] p-6 md:p-10 shadow-sm"
      data-testid="inline-lead-capture"
    >
      {done ? (
        <div className="flex items-start gap-4" data-testid="lead-capture-success">
          <span className="w-12 h-12 rounded-2xl bg-[#064E2E] text-white flex items-center justify-center shrink-0">
            <CheckCircle2 size={24}/>
          </span>
          <div>
            <h3 className="font-display text-2xl text-[#0F172A]">Got it — we'll come back to you</h3>
            <p className="mt-2 text-[#475569] leading-relaxed">
              Thanks for sending this through. We'll take a proper look and come back to you. If anything urgent stands out, we'll flag it straight away.
            </p>
            <p className="mt-3 text-sm text-[#0F766E] italic">
              Most members recover the cost of the plan on their first issue.
            </p>
          </div>
        </div>
      ) : (
        <>
          <h3 className="text-2xl sm:text-3xl font-display font-semibold text-[#0F172A] mb-3">
            Got a Quote or Issue?
          </h3>
          <p className="text-[#475569] mb-6 max-w-2xl">
            We'll take a proper look before you commit. Many quotes include unnecessary work or inflated costs.
          </p>

          <form className="grid gap-4 md:grid-cols-2" onSubmit={onSubmit}>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl border border-[#E2E8F0] bg-white p-3 w-full focus:outline-none focus:border-[#064E2E] focus:ring-2 focus:ring-[#064E2E]/15"
              required
              data-testid="lead-capture-name"
            />
            <input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-[#E2E8F0] bg-white p-3 w-full focus:outline-none focus:border-[#064E2E] focus:ring-2 focus:ring-[#064E2E]/15"
              required
              data-testid="lead-capture-email"
            />

            <label
              className="md:col-span-2 rounded-xl border-2 border-dashed border-[#BFE3E1] bg-white p-4 flex items-center gap-3 cursor-pointer hover:border-[#064E2E] transition"
              data-testid="lead-capture-file-label"
            >
              <Upload size={18} className="text-[#0F766E]"/>
              <span className="text-sm text-[#475569]">
                {file ? (
                  <span className="text-[#0F172A] font-medium flex items-center gap-2">
                    <FileText size={14} className="text-[#064E2E]"/>{file.name}
                  </span>
                ) : "Drop in the quote or photo (optional — PDF, image, video, doc)"}
              </span>
              <input
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.mp4,.mov,.doc,.docx"
                data-testid="lead-capture-file"
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              className="md:col-span-2 rounded-full bg-[#064E2E] text-white py-3 font-semibold hover:bg-[#0F766E] transition flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="lead-capture-submit"
            >
              {busy ? <><Loader2 className="animate-spin" size={16}/> Sending…</> : "Get Your Quote Checked"}
            </button>
          </form>

          <p className="text-sm text-[#64748B] mt-4">
            No obligation. We'll review and highlight anything you should be aware of.
          </p>
          <p className="text-sm text-[#0F766E] italic mt-1">
            Most members recover the cost of the plan on their first issue.
          </p>
        </>
      )}
    </section>
  );
}
